import { type ItineraryComponentOut } from '@utazzo/shared';
import { UzButton, UzDialog } from '@utazzo/ui';
import { Bed, Car, Package, Plane, Ship, Sparkles } from 'lucide-react';
import React from 'react';

import {
  useItineraryComponents,
  useItineraryDetail,
  useMarkupSuggestion,
} from '../use-itineraries';

/**
 * Admin-only "Pricing breakup" dialog. Opens from the itineraries list Actions
 * dropdown ("View pricing"). Renders a per-category table for every
 * component kind (Flights / Hotels / Activities / Transfers / VAS) plus a
 * Selling price matrix that mirrors the legacy PHP itinerary summary popup.
 *
 * Math is the SAME derivation `PricingSidebar` uses so numbers stay
 * consistent between the sidebar-during-editing view and this stand-alone
 * admin popup (product markup + agent share distributed proportionally,
 * GST / TCS / grand-total from the frozen snapshot).
 *
 * Admin-gate lives at the CALL SITE (list page). This component intentionally
 * does not re-check the role - the caller controls when to mount it.
 */

const sumBy = (
  comps: ItineraryComponentOut[],
  kinds: string[],
  field: 'costPaise' | 'markupPaise',
): number =>
  comps.filter((c) => kinds.includes(c.kind)).reduce((s, c) => s + (Number(c[field]) || 0), 0);

/**
 * The "Service" cell for a transfer quote row: the title plus a mode-aware
 * subtitle (Train / Car / ..., the fare class or vehicle, and per-person vs
 * per-vehicle). Legacy car components with no `transferMode` read as CAR.
 */
const transferServiceCell = (c: ItineraryComponentOut): React.ReactNode => {
  const meta = (c.metaJson ?? {}) as Record<string, unknown>;
  const mode = meta.transferMode;
  const isPublic = mode === 'BUS' || mode === 'TRAIN' || mode === 'FERRY';
  const modeLabel =
    mode === 'BUS' ? 'Bus' : mode === 'TRAIN' ? 'Train' : mode === 'FERRY' ? 'Ferry' : 'Car';
  const detail = isPublic
    ? typeof meta.fareClassName === 'string'
      ? meta.fareClassName
      : null
    : typeof meta.vehicleTypeName === 'string'
      ? `${meta.vehicleTypeName}${typeof meta.vehicleCount === 'number' && meta.vehicleCount > 1 ? ` × ${meta.vehicleCount}` : ''}`
      : null;
  const qualifier =
    isPublic || meta.serviceType === 'SIC'
      ? 'per person'
      : meta.serviceType === 'PRIVATE'
        ? 'per vehicle'
        : null;
  const sub = [modeLabel, detail, qualifier].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-col">
      <span>{c.title}</span>
      {sub && <span className="text-[11px] text-zinc-400">{sub}</span>}
    </div>
  );
};

/** The "Ship" cell for a cruise line: ship name plus cabin + deck subtitle,
 *  read straight off the component's `metaJson` (same fields the day-board
 *  `CruiseCard` reads). */
const cruiseServiceCell = (c: ItineraryComponentOut): React.ReactNode => {
  const meta = (c.metaJson ?? {}) as Record<string, unknown>;
  const shipName = typeof meta.shipName === 'string' && meta.shipName ? meta.shipName : c.title;
  const cabinName = typeof meta.cabinName === 'string' ? meta.cabinName : null;
  const deck = typeof meta.deck === 'string' ? meta.deck : null;
  const sub = [cabinName, deck].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-col">
      <span>{shipName}</span>
      {sub && <span className="text-[11px] text-zinc-400">{sub}</span>}
    </div>
  );
};

const fmt = (paise: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(paise / 100);
  } catch {
    return `${currency} ${Math.round(paise / 100).toLocaleString('en-IN')}`;
  }
};

const formatShortDate = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Nights between two ISO dates. `null` when either end is missing. */
const nightsBetween = (start: string | null, end: string | null): number | null => {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)));
};

/** "HH:mm" or "3 hr" style. Best-effort formatter for start/end time strings. */
const formatTime = (t: string | null): string => t?.trim() || '-';

/** Duration between two time strings (HH:mm). Returns "-" if not derivable. */
const durationBetween = (start: string | null, end: string | null): string => {
  if (!start || !end) return '-';
  const parse = (s: string): number | null => {
    const m = s.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return Number(m[1]) * 60 + Number(m[2]);
  };
  const s = parse(start);
  const e = parse(end);
  if (s === null || e === null) return '-';
  const mins = e >= s ? e - s : 24 * 60 - s + e;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h}h ${m}m`;
};

export interface ItineraryPricingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Itinerary id to load. Null while the dialog is closed (skips fetches). */
  itineraryId: string | null;
}

export const ItineraryPricingDialog = ({
  open,
  onOpenChange,
  itineraryId,
}: ItineraryPricingDialogProps): React.JSX.Element => {
  const activeId = open ? itineraryId : null;
  const detailQuery = useItineraryDetail(activeId);
  const componentsQuery = useItineraryComponents(activeId);

  const detail = detailQuery.data ?? null;
  const components = componentsQuery.data ?? [];
  const currency = detail?.currencyCode ?? 'INR';
  const loading = detailQuery.isLoading || componentsQuery.isLoading;

  const flightCount = components.filter((c) => c.kind === 'FLIGHT').length;
  const hotelCount = components.filter((c) => c.kind === 'HOTEL').length;
  const cruiseCount = components.filter((c) => c.kind === 'CRUISE').length;
  const activityCount = components.filter((c) => c.kind === 'ACTIVITY').length;
  const transferCount = components.filter((c) => c.kind === 'TRANSFER').length;

  const scrollToSection = (sectionId: string): void => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      size="full"
      title="Pricing breakup"
      description={detail ? undefined : 'Detailed itinerary price breakdown.'}
      headerExtra={
        detail ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p className="text-muted-foreground min-w-0 text-sm">
              {detail.title} - {detail.numberOfNights}N / {detail.numberOfDays}D
            </p>
            {!loading && (
              <ItemsJumpNav
                items={[
                  {
                    id: 'pricing-flights',
                    label: 'Flights',
                    count: flightCount,
                    tint: 'sky',
                  },
                  {
                    id: 'pricing-hotels',
                    label: 'Hotels',
                    count: hotelCount,
                    tint: 'amber',
                  },
                  {
                    id: 'pricing-cruises',
                    label: 'Cruises',
                    count: cruiseCount,
                    tint: 'cyan',
                  },
                  {
                    id: 'pricing-activities',
                    label: 'Activities',
                    count: activityCount,
                    tint: 'rose',
                  },
                  {
                    id: 'pricing-transfers',
                    label: 'Transfers',
                    count: transferCount,
                    tint: 'emerald',
                  },
                ]}
                onNavigate={scrollToSection}
              />
            )}
          </div>
        ) : undefined
      }
      footer={
        <div className="flex justify-end">
          <UzButton variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </UzButton>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-16 text-sm text-zinc-500">
          Loading pricing details...
        </div>
      ) : !detail ? (
        <div className="flex items-center justify-center py-16 text-sm text-zinc-500">
          Itinerary not found.
        </div>
      ) : (
        <PricingBody detail={detail} components={components} currency={currency} />
      )}
    </UzDialog>
  );
};

// ---------- Body ------------------------------------------------------------

interface PricingBodyProps {
  detail: NonNullable<ReturnType<typeof useItineraryDetail>['data']>;
  components: ItineraryComponentOut[];
  currency: string;
}

const PricingBody = ({ detail, components, currency }: PricingBodyProps): React.JSX.Element => {
  // Live-suggestion vs. frozen snapshot: DRAFT / IN_PROGRESS uses the live
  // suggestion so operators see current markup rates; anything else reads
  // the frozen `detail.markupByCategory` snapshot for consistency with what
  // the customer share page priced at.
  const isDraftLive = detail.status === 'DRAFT' || detail.status === 'IN_PROGRESS';
  const suggestion = useMarkupSuggestion(isDraftLive ? detail.id : null).data ?? null;
  const mbc = (isDraftLive ? suggestion?.markupByCategory : detail.markupByCategory) ?? {};

  // ----- Per-category cost + markup -----
  // Visa + Insurance are not stored as ItineraryComponents - they live on
  // the itinerary row itself (`visaCostPaise` / `insuranceCostPaise`, frozen
  // at save time). Fold both into the VAS bucket for display so the ADDON
  // markup and Grand Total tie out.
  const visaCost = Number(detail.visaCostPaise ?? 0) || 0;
  const insuranceCost = Number(detail.insuranceCostPaise ?? 0) || 0;
  const flightCost = sumBy(components, ['FLIGHT'], 'costPaise');
  const hotelCost = sumBy(components, ['HOTEL'], 'costPaise');
  const cruiseCost = sumBy(components, ['CRUISE'], 'costPaise');
  const activityCost = sumBy(components, ['ACTIVITY'], 'costPaise');
  const transferCost = sumBy(components, ['TRANSFER'], 'costPaise');
  const vasComponentCost = sumBy(components, ['VAS'], 'costPaise');
  const vasCost = vasComponentCost + visaCost + insuranceCost;
  const totalCost = flightCost + hotelCost + cruiseCost + activityCost + transferCost + vasCost;

  const cat = (key: string, kinds: string[]): number =>
    Number(mbc[key] ?? 0) || sumBy(components, kinds, 'markupPaise');
  const flightProductMarkup = cat('FLIGHT', ['FLIGHT']);
  const hotelProductMarkup = cat('HOTEL', ['HOTEL']);
  const cruiseProductMarkup = cat('CRUISE', ['CRUISE']);
  const activityProductMarkup = cat('ACTIVITY', ['ACTIVITY']);
  const transferProductMarkup = cat('TRANSFER', ['TRANSFER']);
  const vasProductMarkup = cat('ADDON', ['VAS']);

  const agentMarkup = Number(detail.agentMarkupPaise ?? 0) || 0;
  const shareOf = (cost: number): number =>
    totalCost > 0 ? Math.round((agentMarkup * cost) / totalCost) : 0;
  const flightAgentShare = shareOf(flightCost);
  const hotelAgentShare = shareOf(hotelCost);
  const cruiseAgentShare = shareOf(cruiseCost);
  const activityAgentShare = shareOf(activityCost);
  const transferAgentShare = shareOf(transferCost);
  const vasAgentShare =
    agentMarkup -
    flightAgentShare -
    hotelAgentShare -
    cruiseAgentShare -
    activityAgentShare -
    transferAgentShare;

  const flightMarkup = flightProductMarkup + flightAgentShare;
  const hotelMarkup = hotelProductMarkup + hotelAgentShare;
  const cruiseMarkup = cruiseProductMarkup + cruiseAgentShare;
  const activityMarkup = activityProductMarkup + activityAgentShare;
  const transferMarkup = transferProductMarkup + transferAgentShare;
  const vasMarkup = vasProductMarkup + vasAgentShare;

  const flightTotal = flightCost + flightMarkup;
  const hotelTotal = hotelCost + hotelMarkup;
  const cruiseTotal = cruiseCost + cruiseMarkup;
  const activityTotal = activityCost + activityMarkup;
  const transferTotal = transferCost + transferMarkup;
  const vasTotal = vasCost + vasMarkup;

  const netCost = totalCost;
  const netMarkup =
    flightMarkup + hotelMarkup + cruiseMarkup + activityMarkup + transferMarkup + vasMarkup;
  const netTotal = netCost + netMarkup;

  const gst = Number(detail.gstPaise ?? 0) || 0;
  const gstRate = detail.gstRatePercent ?? null;
  const tcs = Number(detail.tcsPaise ?? 0) || 0;
  const tcsRate = detail.tcsRatePercent ?? null;
  const discount = Number(detail.discountPaise ?? 0) || 0;
  const packageValueExclTcs = netTotal - discount + gst;

  // Master-level pax counts (used by every per-line table since the
  // component rows don't carry per-line pax splits today).
  const adults = detail.adultsCount ?? 0;
  const children = detail.childrenCount ?? 0;
  const infants = detail.infantsCount ?? 0;

  const flights = components.filter((c) => c.kind === 'FLIGHT');
  const hotels = components.filter((c) => c.kind === 'HOTEL');
  const cruises = components.filter((c) => c.kind === 'CRUISE');
  const activities = components.filter((c) => c.kind === 'ACTIVITY');
  const transfers = components.filter((c) => c.kind === 'TRANSFER');
  const vas = components.filter((c) => c.kind === 'VAS');
  const flightCount = flights.length;
  const hotelCount = hotels.length;
  const cruiseCount = cruises.length;
  const activityCount = activities.length;
  const transferCount = transfers.length;
  const vasLineCount = vas.length + (visaCost > 0 ? 1 : 0) + (insuranceCost > 0 ? 1 : 0);

  return (
    <div className="space-y-5">
      {/* ----- Flights ----- */}
      <CategorySection
        id="pricing-flights"
        icon={<Plane className="h-5 w-5" aria-hidden />}
        tint="sky"
        title="Flight(s)"
        count={flightCount}
        total={flightTotal}
        currency={currency}
        empty={flights.length === 0}
      >
        <SimpleTable
          headers={[
            'Date',
            'Airline / Flight',
            'From',
            'To',
            'Dep',
            'Arr',
            'Refundable?',
            'Fare',
            'Total',
          ]}
          numericColumns={[7, 8]}
          rows={flights.map((c) => [
            formatShortDate(c.startDate),
            c.title + (c.pnr ? ` - ${c.pnr}` : ''),
            c.fromLocation ?? '-',
            c.toLocation ?? '-',
            formatTime(c.startTime),
            formatTime(c.endTime),
            c.isRefundable ? 'Yes' : 'No',
            fmt(Number(c.costPaise) || 0, currency),
            fmt((Number(c.costPaise) || 0) + (Number(c.markupPaise) || 0), currency),
          ])}
        />
      </CategorySection>

      {/* ----- Hotels ----- */}
      <CategorySection
        id="pricing-hotels"
        icon={<Bed className="h-5 w-5" aria-hidden />}
        tint="amber"
        title="Hotels"
        count={hotelCount}
        total={hotelTotal}
        currency={currency}
        empty={hotels.length === 0}
      >
        <SimpleTable
          headers={['Hotel', 'Check-in', 'Check-out', 'Nights', 'Cost', 'Total']}
          numericColumns={[3, 4, 5]}
          rows={hotels.map((c) => {
            const nights = nightsBetween(c.startDate, c.endDate);
            return [
              c.title,
              formatShortDate(c.startDate),
              formatShortDate(c.endDate),
              nights ?? '-',
              fmt(Number(c.costPaise) || 0, currency),
              fmt((Number(c.costPaise) || 0) + (Number(c.markupPaise) || 0), currency),
            ];
          })}
        />
      </CategorySection>

      {/* ----- Cruises (cruise-as-stay Phase 2) ----- */}
      <CategorySection
        id="pricing-cruises"
        icon={<Ship className="h-5 w-5" aria-hidden />}
        tint="cyan"
        title="Cruise(s)"
        count={cruiseCount}
        total={cruiseTotal}
        currency={currency}
        empty={cruises.length === 0}
      >
        <SimpleTable
          headers={['Ship / Cabin', 'Check-in', 'Check-out', 'Nights', 'Cost', 'Total']}
          numericColumns={[3, 4, 5]}
          rows={cruises.map((c) => {
            const nights = nightsBetween(c.startDate, c.endDate);
            return [
              cruiseServiceCell(c),
              formatShortDate(c.startDate),
              formatShortDate(c.endDate),
              nights ?? '-',
              fmt(Number(c.costPaise) || 0, currency),
              fmt((Number(c.costPaise) || 0) + (Number(c.markupPaise) || 0), currency),
            ];
          })}
        />
      </CategorySection>

      {/* ----- Activities ----- */}
      <CategorySection
        id="pricing-activities"
        icon={<Sparkles className="h-5 w-5" aria-hidden />}
        tint="rose"
        title="Activities"
        count={activityCount}
        total={activityTotal}
        currency={currency}
        empty={activities.length === 0}
      >
        <SimpleTable
          headers={['Activity', 'Date', 'From', 'To', 'Cost', 'Total']}
          numericColumns={[4, 5]}
          rows={activities.map((c) => [
            c.title,
            formatShortDate(c.startDate),
            c.fromLocation ?? '-',
            c.toLocation ?? '-',
            fmt(Number(c.costPaise) || 0, currency),
            fmt((Number(c.costPaise) || 0) + (Number(c.markupPaise) || 0), currency),
          ])}
        />
      </CategorySection>

      {/* ----- Transfers ----- */}
      <CategorySection
        id="pricing-transfers"
        icon={<Car className="h-5 w-5" aria-hidden />}
        tint="emerald"
        title="Transfers"
        count={transferCount}
        total={transferTotal}
        currency={currency}
        empty={transfers.length === 0}
      >
        <SimpleTable
          headers={[
            'Date',
            'Service',
            'Duration',
            'Adults',
            'Childs',
            'Infants',
            'Refundable?',
            'Price',
            'Total',
          ]}
          numericColumns={[3, 4, 5, 7, 8]}
          rows={transfers.map((c) => [
            formatShortDate(c.startDate),
            transferServiceCell(c),
            durationBetween(c.startTime, c.endTime),
            adults,
            children,
            infants,
            c.isRefundable ? 'Yes' : 'No',
            fmt(Number(c.costPaise) || 0, currency),
            fmt((Number(c.costPaise) || 0) + (Number(c.markupPaise) || 0), currency),
          ])}
        />
      </CategorySection>

      {/* ----- VAS (visa / insurance / add-ons) -----
          Visa + Insurance rows are synthesised from detail.visaCostPaise /
          detail.insuranceCostPaise since neither is stored as an
          ItineraryComponent. Their markup is folded into the ADDON bucket
          alongside VAS-component markup, so no separate markup breakdown
          is shown here (the total column reflects everything). */}
      <CategorySection
        icon={<Package className="h-5 w-5" aria-hidden />}
        tint="violet"
        title="VAS (Visa / Insurance / Add-ons)"
        count={vasLineCount}
        total={vasTotal}
        currency={currency}
        empty={vas.length === 0 && visaCost === 0 && insuranceCost === 0}
      >
        <SimpleTable
          headers={['Add-on', 'Date', 'Cost', 'Total']}
          numericColumns={[2, 3]}
          rows={[
            ...(visaCost > 0
              ? [
                  [
                    (detail.visaName ?? 'Visa') + ' (visa)',
                    '-',
                    fmt(visaCost, currency),
                    fmt(visaCost, currency),
                  ] as React.ReactNode[],
                ]
              : []),
            ...(insuranceCost > 0
              ? [
                  [
                    (detail.insuranceName ?? 'Insurance') + ' (insurance)',
                    '-',
                    fmt(insuranceCost, currency),
                    fmt(insuranceCost, currency),
                  ] as React.ReactNode[],
                ]
              : []),
            ...vas.map(
              (c) =>
                [
                  c.title,
                  formatShortDate(c.startDate),
                  fmt(Number(c.costPaise) || 0, currency),
                  fmt((Number(c.costPaise) || 0) + (Number(c.markupPaise) || 0), currency),
                ] as React.ReactNode[],
            ),
          ]}
        />
      </CategorySection>

      {/* ----- Selling price matrix ----- */}
      <section className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <header className="border-b border-zinc-100 bg-zinc-50/70 px-4 py-3">
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Selling price</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Category-wise cost, markup and total. GST + TCS applied at the bottom.
          </p>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50/50 text-left">
                <th className="px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  Category
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  Cost
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  Markup
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              <SummaryRow
                label={`Flight Cost (${flightCount})`}
                cost={flightCost}
                markup={flightMarkup}
                total={flightTotal}
                currency={currency}
              />
              <SummaryRow
                label={`Hotel Cost (${hotelCount})`}
                cost={hotelCost}
                markup={hotelMarkup}
                total={hotelTotal}
                currency={currency}
              />
              <SummaryRow
                label={`Cruise Cost (${cruiseCount})`}
                cost={cruiseCost}
                markup={cruiseMarkup}
                total={cruiseTotal}
                currency={currency}
              />
              <SummaryRow
                label={`Activity Cost (${activityCount})`}
                cost={activityCost}
                markup={activityMarkup}
                total={activityTotal}
                currency={currency}
              />
              <SummaryRow
                label={`Transfer Cost (${transferCount})`}
                cost={transferCost}
                markup={transferMarkup}
                total={transferTotal}
                currency={currency}
              />
              <SummaryRow
                label={`VAS Cost (${vasLineCount})`}
                cost={vasCost}
                markup={vasMarkup}
                total={vasTotal}
                currency={currency}
              />
              <tr className="border-t-2 border-zinc-200 bg-zinc-50/50 font-semibold">
                <td className="px-4 py-2.5 text-zinc-900">Net Cost Price</td>
                <td className="px-4 py-2.5 text-right font-mono text-zinc-900 tabular-nums">
                  {fmt(netCost, currency)}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-zinc-900 tabular-nums">
                  {fmt(netMarkup, currency)}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-zinc-900 tabular-nums">
                  {fmt(netTotal, currency)}
                </td>
              </tr>
              {discount > 0 && (
                <tr className="border-t border-zinc-100 text-zinc-700">
                  <td className="px-4 py-2 pl-6" colSpan={3}>
                    Discount
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">
                    - {fmt(discount, currency)}
                  </td>
                </tr>
              )}
              <tr className="border-t border-zinc-100 text-zinc-700">
                <td className="px-4 py-2 pl-6" colSpan={3}>
                  GST{gstRate !== null ? ` (@${gstRate}%)` : ''}
                </td>
                <td className="px-4 py-2 text-right font-mono tabular-nums">
                  {fmt(gst, currency)}
                </td>
              </tr>
              <tr className="border-t border-zinc-100 text-zinc-700">
                <td className="px-4 py-2 pl-6" colSpan={3}>
                  TCS{tcsRate !== null ? ` (@${tcsRate}%)` : ''}
                </td>
                <td className="px-4 py-2 text-right font-mono tabular-nums">
                  {fmt(tcs, currency)}
                </td>
              </tr>
              <tr className="border-t-2 border-zinc-200 bg-emerald-50/50 text-base font-semibold">
                <td className="px-4 py-3 text-zinc-900" colSpan={3}>
                  Total Itinerary Package Value (incl. all taxes, excl. TCS)
                </td>
                <td className="px-4 py-3 text-right font-mono text-zinc-900 tabular-nums">
                  {fmt(packageValueExclTcs, currency)}
                </td>
              </tr>
              <tr className="border-t border-zinc-100 text-sm text-zinc-600">
                <td className="px-4 py-2" colSpan={3}>
                  Grand Total (incl. TCS)
                </td>
                <td className="px-4 py-2 text-right font-mono tabular-nums">
                  {fmt(Number(detail.grandTotalPaise) || 0, currency)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

// ---------- Sub-components --------------------------------------------------

type CategoryTint = 'sky' | 'amber' | 'rose' | 'emerald' | 'violet' | 'cyan';

interface ItemsJumpNavProps {
  items: { id: string; label: string; count: number; tint: CategoryTint }[];
  onNavigate: (sectionId: string) => void;
}

const JUMP_LINK_CLASSES: Record<CategoryTint, string> = {
  sky: 'text-sky-700 decoration-sky-300 hover:bg-sky-50 hover:decoration-sky-500',
  amber: 'text-amber-700 decoration-amber-300 hover:bg-amber-50 hover:decoration-amber-500',
  rose: 'text-rose-700 decoration-rose-300 hover:bg-rose-50 hover:decoration-rose-500',
  emerald:
    'text-emerald-700 decoration-emerald-300 hover:bg-emerald-50 hover:decoration-emerald-500',
  violet: 'text-violet-700 decoration-violet-300 hover:bg-violet-50 hover:decoration-violet-500',
  cyan: 'text-cyan-700 decoration-cyan-300 hover:bg-cyan-50 hover:decoration-cyan-500',
};

/** Inline jump links shown beside the dialog subheading. */
const ItemsJumpNav = ({ items, onNavigate }: ItemsJumpNavProps): React.JSX.Element => (
  <nav
    aria-label="Jump to pricing section"
    className="flex shrink-0 flex-wrap items-center gap-x-0 text-sm"
  >
    {items.map((item, i) => (
      <React.Fragment key={item.id}>
        {i > 0 && (
          <span className="px-1.5 text-zinc-300" aria-hidden>
            ,
          </span>
        )}
        <button
          type="button"
          onClick={() => onNavigate(item.id)}
          title={`Scroll to ${item.label}`}
          className={`cursor-pointer rounded-md px-1.5 py-0.5 font-medium underline decoration-dotted underline-offset-4 transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:outline-none ${JUMP_LINK_CLASSES[item.tint]}`}
        >
          {item.label} <span className="font-semibold tabular-nums">{item.count}</span>
        </button>
      </React.Fragment>
    ))}
  </nav>
);

/**
 * Colored icon-pill palette per category. Keeps the icon prominent (matching
 * the legacy popup's emoji-with-colour anchor) while staying inside the
 * design system's tailwind palette.
 */
const TINT_CLASSES: Record<
  CategoryTint,
  { chip: string; icon: string; totalText: string; headerBg: string }
> = {
  sky: {
    chip: 'bg-sky-100 text-sky-700 ring-1 ring-sky-200',
    icon: 'text-sky-600',
    totalText: 'text-sky-700',
    headerBg: 'bg-sky-50/60',
  },
  amber: {
    chip: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
    icon: 'text-amber-600',
    totalText: 'text-amber-700',
    headerBg: 'bg-amber-50/60',
  },
  rose: {
    chip: 'bg-rose-100 text-rose-700 ring-1 ring-rose-200',
    icon: 'text-rose-600',
    totalText: 'text-rose-700',
    headerBg: 'bg-rose-50/60',
  },
  emerald: {
    chip: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
    icon: 'text-emerald-600',
    totalText: 'text-emerald-700',
    headerBg: 'bg-emerald-50/60',
  },
  violet: {
    chip: 'bg-violet-100 text-violet-700 ring-1 ring-violet-200',
    icon: 'text-violet-600',
    totalText: 'text-violet-700',
    headerBg: 'bg-violet-50/60',
  },
  cyan: {
    chip: 'bg-cyan-100 text-cyan-700 ring-1 ring-cyan-200',
    icon: 'text-cyan-600',
    totalText: 'text-cyan-700',
    headerBg: 'bg-cyan-50/60',
  },
};

interface CategorySectionProps {
  /** Anchor id for jump-nav scroll targets. */
  id?: string;
  icon: React.ReactNode;
  title: string;
  /** Item count shown next to the title, e.g. Flight(s) (2). */
  count?: number;
  total: number;
  currency: string;
  empty: boolean;
  tint: CategoryTint;
  children: React.ReactNode;
}

const CategorySection = ({
  id,
  icon,
  title,
  count,
  total,
  currency,
  empty,
  tint,
  children,
}: CategorySectionProps): React.JSX.Element => {
  const t = TINT_CLASSES[tint];
  return (
    <section
      id={id}
      className="scroll-mt-4 overflow-hidden rounded-xl border border-zinc-200 bg-white"
    >
      <header
        className={`flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-3 ${t.headerBg}`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.chip}`}
            aria-hidden
          >
            <span className={t.icon}>{icon}</span>
          </span>
          <h3 className="text-base font-semibold tracking-tight text-zinc-900">
            {title}
            {count !== undefined && (
              <span className="ml-1.5 font-medium text-zinc-500 tabular-nums">({count})</span>
            )}
          </h3>
        </div>
        <span className={`font-mono text-base font-bold tabular-nums ${t.totalText}`}>
          {fmt(total, currency)}
        </span>
      </header>
      {empty ? (
        <div className="px-4 py-6 text-sm text-zinc-400">No records to display</div>
      ) : (
        children
      )}
    </section>
  );
};

interface SimpleTableProps {
  headers: string[];
  rows: React.ReactNode[][];
  /** 0-based column indexes that should be right-aligned + monospace. */
  numericColumns?: number[];
}

const SimpleTable = ({
  headers,
  rows,
  numericColumns = [],
}: SimpleTableProps): React.JSX.Element => {
  const numSet = new Set(numericColumns);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100 bg-zinc-50/50 text-left">
            {headers.map((h, i) => (
              <th
                key={h}
                className={
                  'px-4 py-2.5 text-xs font-medium tracking-wide text-zinc-500 uppercase ' +
                  (numSet.has(i) ? 'text-right' : '')
                }
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, ri) => (
            <tr key={ri} className="border-b border-zinc-50 last:border-b-0 hover:bg-zinc-50/50">
              {cells.map((cell, ci) => (
                <td
                  key={ci}
                  className={
                    'px-4 py-2.5 text-zinc-700 ' +
                    (numSet.has(ci) ? 'text-right font-mono text-zinc-900 tabular-nums' : '')
                  }
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface SummaryRowProps {
  label: string;
  cost: number;
  markup: number;
  total: number;
  currency: string;
}

const SummaryRow = ({
  label,
  cost,
  markup,
  total,
  currency,
}: SummaryRowProps): React.JSX.Element => (
  <tr className="border-b border-zinc-50 last:border-b-0">
    <td className="px-4 py-2.5 text-zinc-700">{label}</td>
    <td className="px-4 py-2.5 text-right font-mono text-zinc-700 tabular-nums">
      {fmt(cost, currency)}
    </td>
    <td className="px-4 py-2.5 text-right font-mono text-zinc-700 tabular-nums">
      {fmt(markup, currency)}
    </td>
    <td className="px-4 py-2.5 text-right font-mono font-medium text-zinc-900 tabular-nums">
      {fmt(total, currency)}
    </td>
  </tr>
);
